import { apiClient } from '@api/apiClient';
import { V4ApiResponse } from '@api/types';
import { buildChangeTrackingRequestConfig } from '@changeTracking/utils/buildChangeTrackingHeaders';
import type { ChangeTrackingSource } from '@typeDefinitions/changeTrackingTypes';
import { logError } from '@utils/errorLogging';
import { createError } from '@utils/errorUtils';

import { CollectionFlag, CollectionFlagPayload } from './types';

const isNotFoundError = (error: unknown) =>
  Array.isArray(error) &&
  error.some(
    (item: { propertyName?: string }) => item.propertyName === 'NotFound',
  );

export const fetchCollectionFlags = async (
  customerId: number,
): Promise<CollectionFlag[]> => {
  try {
    const response: CollectionFlag[] = await apiClient.get(
      `/CollectionFlag/all?customerId=${customerId}`,
    );
    return response;
  } catch (error: unknown) {
    if (isNotFoundError(error)) {
      return [];
    }
    logError('fetchCollectionFlags', error as Error);
    throw createError(error, 'Failed to fetch collection flags');
  }
};

export const createCollectionFlag = async (
  payload: CollectionFlagPayload,
  source: ChangeTrackingSource,
) => {
  try {
    const response: V4ApiResponse<CollectionFlag> = await apiClient.post(
      '/CollectionFlag/collectionflagcreate',
      payload,
      buildChangeTrackingRequestConfig([source]),
    );
    if (response.isSuccess) {
      return response.value;
    }
    throw response.errors;
  } catch (error: unknown) {
    logError('createCollectionFlag', error as Error);
    throw createError(error, 'Failed to create collection flag');
  }
};

export const updateCollectionFlag = async (
  payload: CollectionFlagPayload,
  source: ChangeTrackingSource,
) => {
  try {
    const response: V4ApiResponse<CollectionFlag> = await apiClient.put(
      '/CollectionFlag',
      payload,
      buildChangeTrackingRequestConfig([source]),
    );
    if (response.isSuccess) {
      return response.value;
    }
    throw response.errors;
  } catch (error: unknown) {
    logError('updateCollectionFlag', error as Error);
    throw createError(error, 'Failed to update collection flag');
  }
};

export const deleteCollectionFlag = async (
  id: number,
  source: ChangeTrackingSource,
) => {
  try {
    const response: V4ApiResponse<boolean> = await apiClient.delete(
      `/CollectionFlag/${id}`,
      buildChangeTrackingRequestConfig([source]),
    );
    if (response.isSuccess) {
      return;
    }
    throw response.errors;
  } catch (error: unknown) {
    logError('deleteCollectionFlag', error as Error);
    throw createError(error, 'Failed to delete collection flag');
  }
};
